import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, MapPin, Users, Fuel, Cog, ShieldCheck, BadgeCheck, Zap, ChevronLeft, CarFront, Truck, UserCheck, ArrowRight, Gauge } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import api, { formatZAR, formatRate, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { format, addDays, differenceInCalendarDays } from "date-fns";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [range, setRange] = useState({ from: new Date(Date.now() + 24 * 3600 * 1000), to: addDays(new Date(), 4) });
  const [withDriver, setWithDriver] = useState(false);
  const [withDelivery, setWithDelivery] = useState(false);
  const [insuranceAddon, setInsuranceAddon] = useState(true);
  const [quote, setQuote] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [booking, setBooking] = useState(false);
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("");

  useEffect(() => {
    api.get("/payments/providers").then((r) => {
      const avail = (r.data.providers || []).filter((p) => p.available);
      setProviders(avail);
      setProvider(avail.some((p) => p.id === r.data.default) ? r.data.default : avail[0]?.id || "stripe");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => navigate("/vehicles"));
  }, [id, navigate]);

  const fetchQuote = async () => {
    if (!v || !range?.from || !range?.to || !user?.id) return;
    try {
      const { data } = await api.post("/bookings/quote", {
        vehicle_id: v.id,
        start_date: range.from.toISOString(),
        end_date: range.to.toISOString(),
        with_driver: withDriver,
        with_delivery: withDelivery,
        insurance_addon: insuranceAddon,
        origin_url: window.location.origin,
      });
      setQuote(data);
    } catch (_) {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchQuote(); }, [v, range, withDriver, withDelivery, insuranceAddon, user]);

  const proceed = async () => {
    if (!user?.id) {
      navigate(`/login?next=/vehicles/${id}`);
      return;
    }
    if (!range?.from || !range?.to) {
      toast.error("Please select your trip dates first");
      return;
    }
    setBooking(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        vehicle_id: v.id,
        start_date: range.from.toISOString(),
        end_date: range.to.toISOString(),
        with_driver: withDriver,
        with_delivery: withDelivery,
        insurance_addon: insuranceAddon,
        origin_url: window.location.origin,
        provider,
      });
      if (data.provider === "payfast") {
        // Hosted redirect: a plain form POST to PayFast with the server-signed fields
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.action;
        Object.entries(data.fields).forEach(([name, value]) => {
          const input = document.createElement("input");
          input.type = "hidden"; input.name = name; input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBooking(false);
    }
  };

  const localQuote = useMemo(() => quote?.pricing, [quote]);
  const tripDays = localQuote?.days || (range?.from && range?.to ? Math.max(1, differenceInCalendarDays(range.to, range.from)) : 0);

  if (!v) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-[#64748B]">Loading vehicle…</div>;
  }

  const photos = v.photos && v.photos.length ? v.photos : ["https://images.pexels.com/photos/27533382/pexels-photo-27533382.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"];

  return (
    <div className="relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-8">
        <Link to="/vehicles" className="inline-flex items-center gap-1 text-sm font-semibold text-[#64748B] hover:text-[#08153a]" data-testid="back-link">
          <ChevronLeft size={14} /> Back to results
        </Link>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="rounded-[1.75rem] overflow-hidden bg-[#F0F2F5] aspect-[16/10]">
              <img src={photos[activePhoto]} alt={v.title} className="w-full h-full object-cover" />
            </div>
            {photos.length > 1 && (
              <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
                {photos.map((p, i) => (
                  <button key={i} onClick={() => setActivePhoto(i)} className={`flex-shrink-0 w-24 aspect-[4/3] rounded-xl overflow-hidden border-2 ${i === activePhoto ? "border-[#1d7ffd]" : "border-transparent"}`}>
                    <img src={p} alt={`${i}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-[1.75rem] border border-[#E2E8F0] p-6 sticky top-24 bg-white shadow-[0_8px_24px_-12px_rgba(8,21,58,0.08)]">
              <div className="flex items-baseline gap-1">
                <span className="font-display text-3xl font-black text-[#08153a]">{formatZAR(v.price_per_day)}</span>
                <span className="text-sm text-[#64748B]">/day</span>
              </div>
              {v.rating_avg > 0 && (
                <p className="text-xs text-[#64748B] mt-1 inline-flex items-center gap-1"><Star size={12} className="fill-[#08153a] text-[#08153a]" /> {v.rating_avg} · {v.rating_count} reviews</p>
              )}
              <p className="text-xs text-[#0a1f4d] mt-2 flex items-start gap-1.5 bg-[#F4F8FF] rounded-lg px-2.5 py-1.5 leading-relaxed" data-testid="detail-km-allowance">
                <Gauge size={13} className="text-[#1d7ffd] mt-0.5 flex-shrink-0" />
                <span>
                  <b>{v.km_per_day} km/day included</b> · {formatRate(v.excess_km_rate)}/km after
                  {tripDays > 0 && <span className="text-[#64748B]"> · {(tripDays * v.km_per_day).toLocaleString()} km for {tripDays} {tripDays === 1 ? "day" : "days"}</span>}
                </span>
              </p>

              <div className="mt-5 grid grid-cols-2 gap-2 border border-[#E2E8F0] rounded-2xl overflow-hidden">
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="text-left p-3 hover:bg-[#F7F8FA]" data-testid="detail-from-btn">
                      <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B]">Pickup</p>
                      <p className="text-sm font-semibold text-[#08153a]">{range?.from ? format(range.from, "EEE, d MMM") : "—"}</p>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="p-0">
                    <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="text-left p-3 hover:bg-[#F7F8FA] border-l border-[#E2E8F0]" data-testid="detail-to-btn">
                      <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B]">Return</p>
                      <p className="text-sm font-semibold text-[#08153a]">{range?.to ? format(range.to, "EEE, d MMM") : "—"}</p>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="p-0">
                    <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="mt-5 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={insuranceAddon} onCheckedChange={(c) => setInsuranceAddon(!!c)} data-testid="opt-insurance" />
                  <div>
                    <p className="text-sm font-semibold text-[#08153a]">Premium insurance</p>
                    <p className="text-xs text-[#64748B]">Lower excess, R95/day</p>
                  </div>
                </label>
                {v.driver_available && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={withDriver} onCheckedChange={(c) => setWithDriver(!!c)} data-testid="opt-driver" />
                    <div>
                      <p className="text-sm font-semibold text-[#08153a]">Add a chauffeur</p>
                      <p className="text-xs text-[#64748B]">{formatZAR(v.driver_fee_per_day)}/day</p>
                    </div>
                  </label>
                )}
                {v.delivery_available && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={withDelivery} onCheckedChange={(c) => setWithDelivery(!!c)} data-testid="opt-delivery" />
                    <div>
                      <p className="text-sm font-semibold text-[#08153a]">Deliver to me</p>
                      <p className="text-xs text-[#64748B]">{formatZAR(v.delivery_fee)} flat</p>
                    </div>
                  </label>
                )}
              </div>

              {localQuote && (
                <div className="mt-6 pt-5 border-t border-[#E2E8F0] space-y-2 text-sm">
                  <Row label={`${formatZAR(v.price_per_day)} × ${localQuote.days} days`} value={formatZAR(localQuote.rental)} />
                  {localQuote.insurance > 0 && <Row label="Premium insurance" value={formatZAR(localQuote.insurance)} />}
                  {localQuote.driver > 0 && <Row label="Chauffeur" value={formatZAR(localQuote.driver)} />}
                  {localQuote.delivery > 0 && <Row label="Delivery" value={formatZAR(localQuote.delivery)} />}
                  <Row label="Service fee" value={formatZAR(localQuote.service_fee)} />
                  <Row label="Refundable deposit" value={formatZAR(localQuote.deposit)} subtle />
                  <div className="flex items-start gap-2 rounded-xl bg-[#F4F8FF] px-3 py-2.5 text-xs text-[#0a1f4d]" data-testid="quote-km-allowance">
                    <Gauge size={14} className="text-[#1d7ffd] mt-0.5 flex-shrink-0" />
                    <span>
                      <b>{localQuote.km_included.toLocaleString()} km included</b> for {localQuote.days} {localQuote.days === 1 ? "day" : "days"} ({localQuote.km_per_day} km/day) · {formatRate(localQuote.excess_km_rate)}/km after that
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between pt-3 border-t border-[#E2E8F0]">
                    <span className="font-display font-bold text-[#08153a]">Total due</span>
                    <span className="font-display text-xl font-black text-[#08153a]">{formatZAR(localQuote.total_due)}</span>
                  </div>
                </div>
              )}

              {providers.length > 1 && (
                <div className="mt-5" data-testid="payment-provider-picker">
                  <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] mb-2">Pay with</p>
                  <div className="grid grid-cols-2 gap-2">
                    {providers.map((p) => (
                      <button key={p.id} type="button" onClick={() => setProvider(p.id)} className={`text-left p-3 rounded-xl border transition ${provider === p.id ? "border-[#1d7ffd] bg-[#1d7ffd]/5" : "border-[#E2E8F0] hover:border-[#08153a]"}`} data-testid={`provider-${p.id}`}>
                        <p className="text-sm font-bold text-[#08153a]">{p.label}</p>
                        <p className="text-[11px] text-[#64748B] leading-tight mt-0.5">{p.note}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={proceed} disabled={booking} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="book-vehicle-cta">
                {booking ? "Redirecting…" : (<>Continue to payment <ArrowRight size={16} /></>)}
              </button>
              <p className="text-[11px] text-[#64748B] text-center mt-3">{provider === "payfast" ? "Secure payment via PayFast — cards, Instant EFT & SnapScan. You won't be charged until you confirm there." : "You won't be charged until you confirm on the next step."}</p>
            </div>
          </aside>
        </div>

        {/* Title block */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">{v.vehicle_type} • {v.year}</p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#08153a] mt-2">{v.title}</h1>
            <p className="mt-3 text-sm text-[#64748B] inline-flex items-center gap-1"><MapPin size={14} /> {v.location_text}, {v.city}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {v.inspection_certified && <Pill icon={<BadgeCheck size={12} />} label="Certified inspection" />}
              {v.insurance_included && <Pill icon={<ShieldCheck size={12} />} label="Insurance attached" />}
              {v.instant_book && <Pill icon={<Zap size={12} />} label="Instant book" />}
              {v.delivery_available && <Pill icon={<Truck size={12} />} label="Delivery available" />}
              {v.driver_available && <Pill icon={<UserCheck size={12} />} label="Chauffeur available" />}
            </div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Spec icon={<Cog size={16} />} label="Transmission" value={v.transmission} />
              <Spec icon={<Fuel size={16} />} label="Fuel" value={v.fuel_type} />
              <Spec icon={<Users size={16} />} label="Seats" value={`${v.seats}`} />
              <Spec icon={<CarFront size={16} />} label="Type" value={v.vehicle_type} />
              <Spec icon={<Gauge size={16} />} label="Included km" value={`${v.km_per_day} km/day`} />
            </div>

            <div className="mt-10">
              <h2 className="font-display text-xl font-bold text-[#08153a]">About this car</h2>
              <p className="mt-3 text-[#334155] leading-relaxed">{v.description}</p>
            </div>

            {v.features && v.features.length > 0 && (
              <div className="mt-8">
                <h3 className="font-display text-lg font-bold text-[#08153a] mb-3">Features</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {v.features.map((f) => (
                    <div key={f} className="text-sm text-[#08153a] inline-flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1d7ffd]" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12">
              <div className="rounded-2xl bg-[#F7F8FA] border border-[#E2E8F0] p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#08153a] text-white flex items-center justify-center font-bold">{(v.owner_name || "H").slice(0,1)}</div>
                <div>
                  <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Hosted by</p>
                  <p className="font-display font-bold text-[#08153a]">{v.owner_name || "Host"}</p>
                </div>
                <div className="ml-auto chip"><ShieldCheck size={12} className="text-[#1d7ffd]" /> Verified host</div>
              </div>
            </div>

            {v.reviews && v.reviews.length > 0 && (
              <div className="mt-12">
                <h2 className="font-display text-xl font-bold text-[#08153a] mb-4">Reviews</h2>
                <div className="space-y-4">
                  {v.reviews.map((r) => (
                    <div key={r.id} className="rounded-2xl border border-[#E2E8F0] p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#F0F2F5] text-[#08153a] font-bold flex items-center justify-center">{(r.user_name || "U").slice(0,1)}</div>
                        <div>
                          <p className="text-sm font-semibold text-[#08153a]">{r.user_name}</p>
                          <p className="text-xs text-[#64748B]">{format(new Date(r.created_at), "d MMM yyyy")}</p>
                        </div>
                        <div className="ml-auto inline-flex items-center gap-1 text-sm text-[#08153a]"><Star size={13} className="fill-[#08153a] text-[#08153a]" /> {r.rating}</div>
                      </div>
                      {r.comment && <p className="text-sm text-[#334155] mt-3 leading-relaxed">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, subtle }) => (
  <div className={`flex items-center justify-between ${subtle ? "text-[#64748B]" : "text-[#334155]"}`}>
    <span>{label}</span>
    <span className="font-semibold text-[#08153a]">{value}</span>
  </div>
);

const Spec = ({ icon, label, value }) => (
  <div className="rounded-2xl border border-[#E2E8F0] p-4">
    <div className="text-[#1d7ffd]">{icon}</div>
    <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] mt-2">{label}</p>
    <p className="text-sm font-semibold text-[#08153a] capitalize">{value}</p>
  </div>
);

const Pill = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1.5 bg-[#F0F2F5] text-[#08153a] text-xs font-semibold px-3 py-1.5 rounded-md">
    <span className="text-[#1d7ffd]">{icon}</span> {label}
  </span>
);
